import { suggestionsFor, SUGGEST_ALL } from '../../../utils/variableSuggestions';

/**
 * Resolve dropdown state when the caret follows `{{…`, `{{$…`, or a bare `$…`.
 *
 * `scopes` is what the user has saved; those are offered first, and the `$`
 * generated variables below them (utils/variableSuggestions).
 */
/**
 * The suggestion panel's clamp, mirroring `.dyn-var-suggestions` in index.css.
 *
 * Duplicated here on purpose: the panel is positioned in JS and sized in CSS,
 * so the placement maths needs the numbers. Change one and change the other —
 * `variableDropdownPicking.check.js` measures the rendered box against the
 * field and fails if they drift apart.
 */
/**
 * How much of a bare word must be typed before it searches the variables.
 *
 * One: the owner asked for it to filter ALWAYS and to hide when nothing
 * matches, rather than waiting for `{{` or `$` (23.09.2026). Matching is what
 * keeps this quiet — a value like `application/json` matches nothing and the
 * list closes — so the length guard exists only as the one knob to turn if the
 * first keystroke ever proves too eager.
 */
export const BARE_WORD_MIN_CHARS = 1;

/** Suggest on anything typed (params, auth) vs only inside `{{` (the URL). */
export const TRIGGER_ALWAYS = 'always';

export const TRIGGER_BRACES = 'braces';

export const DYN_SUGGESTIONS_MAX_HEIGHT = 220;

export const DYN_SUGGESTIONS_MAX_WIDTH = 340;

/**
 * Is the caret sitting inside a `{{name}}` that is already complete?
 *
 * Only a POINTER asks this. Clicking a finished reference used to flash the
 * suggestion panel — one row, the variable already written there — in the gap
 * before the editor popup opened, and the hover tooltip had just said the same
 * thing (owner, 23.09.2026). Typing is deliberately exempt: editing a name in
 * place still filters the list, which is the whole point of the inner mode.
 */
export function caretInsideCompleteReference(text, cursorPos) {
  const before = text.slice(0, cursorPos);
  const openIdx = before.lastIndexOf('{{');
  if (openIdx === -1) return false;
  // A `}}` between that `{{` and the caret means the reference already ended.
  if (before.indexOf('}}', openIdx) !== -1) return false;
  const after = text.slice(cursorPos);
  const closeIdx = after.indexOf('}}');
  if (closeIdx === -1) return false;
  // A `{{` before the closing braces means the caret is in an unfinished one.
  const nextOpen = after.indexOf('{{');
  return nextOpen === -1 || nextOpen > closeIdx;
}

export function resolveDynSuggestionState(textBefore, cursorPos, scopes, include = SUGGEST_ALL, trigger = TRIGGER_ALWAYS) {
  // Inside `{{ … }}`: everything typed since the braces is the prefix, whether
  // or not it starts with `$`. It used to require a `$`, so a half-typed name
  // of your own offered nothing.
  const braceMatch = textBefore.match(/\{\{([^}{]*)$/);
  if (braceMatch) {
    const prefix = braceMatch[1];
    const items = suggestionsFor(prefix, scopes, include);
    if (!items.length) return null;
    return {
      items,
      insertStart: cursorPos - prefix.length,
      insertEnd: cursorPos,
      mode: 'inner',
    };
  }

  // A field can ask to be woken ONLY by `{{`. The URL does: it is long, mostly
  // not variables, and a list over every word typed there is in the way
  // (owner, 23.09.2026).
  if (trigger === TRIGGER_BRACES) return null;

  // Outside braces, a bare `$…` offers the generated ones and wraps the
  // insertion in braces itself.
  const bareMatch = textBefore.match(/(\$\w*)$/);
  if (bareMatch) {
    const prefix = bareMatch[1];
    const items = suggestionsFor(prefix, scopes, include);
    if (!items.length) return null;
    return {
      items,
      insertStart: cursorPos - prefix.length,
      insertEnd: cursorPos,
      mode: 'wrap',
    };
  }

  // A bare WORD live-searches too — the list filters ALWAYS, not only after
  // `{{` or `$` (owner, 23.09.2026).
  //
  // What keeps this from being noise is that it only survives when something
  // MATCHES: a value like `application/json` matches nothing and this returns
  // null, which closes the list. Delete a character and the matches come back,
  // because every edit re-runs this from scratch.
  const wordMatch = textBefore.match(/([A-Za-z_][\w-]*)$/);
  if (wordMatch && wordMatch[1].length >= BARE_WORD_MIN_CHARS) {
    const prefix = wordMatch[1];
    const items = suggestionsFor(prefix, scopes, include);
    if (!items.length) return null;
    return {
      items,
      insertStart: cursorPos - prefix.length,
      insertEnd: cursorPos,
      mode: 'wrap',
    };
  }

  return null;
}

export function allSuggestionState(cursorPos, scopes, include = SUGGEST_ALL) {
  return {
    items: suggestionsFor('', scopes, include),
    insertStart: cursorPos,
    insertEnd: cursorPos,
    mode: 'wrap',
  };
}

/**
 * While typing, parent `value` can lag the contenteditable by one React commit.
 * If one side is empty and the other is not, do **not** treat as lag: that happens when
 * switching tabs (new URL or empty URL) while the contenteditable still holds the previous tab’s text.
 * A lagging edit — wherever the caret was, not just at the end — always differs from the
 * previous value by one contiguous insert/delete/replace region, so matching common prefix +
 * common suffix covers the whole string. Anything wider than that is a genuinely different value.
 */
export function textsLookLikeTypingLag(a, b) {
  const sa = a ?? '';
  const sb = b ?? '';
  if (sa === sb) return false;
  if (sa === '' || sb === '') return false;

  const maxLen = Math.min(sa.length, sb.length);
  let prefixLen = 0;
  while (prefixLen < maxLen && sa[prefixLen] === sb[prefixLen]) prefixLen++;

  let suffixLen = 0;
  const maxSuffix = maxLen - prefixLen;
  while (
    suffixLen < maxSuffix &&
    sa[sa.length - 1 - suffixLen] === sb[sb.length - 1 - suffixLen]
  ) {
    suffixLen++;
  }

  return prefixLen + suffixLen >= maxLen;
}

/**
 * Caret to restore when the field expands to the multi-line overlay.
 *
 * Two things go wrong across the expand, and both end with the caret at 0 so
 * the next keystrokes land BEFORE the URL:
 *
 * 1. `getSelectionOffsets()` returns null when the live selection cannot be
 *    resolved at that instant, and skipping the restore leaves the brand-new
 *    node at its default caret.
 * 2. The caret is captured when the overflow is detected, but the overlay node
 *    only mounts a frame or more later. Typing fast lands more characters in
 *    between, so the captured offset is STALE — short of where the caret
 *    actually is by however many characters arrived.
 *
 * `capturedLength` is the text length at capture time; the difference against
 * the text being painted is exactly how far the caret has moved on since, so
 * shifting by it survives any typing speed. Overflow only happens while
 * typing, so the end of the text is the right answer when nothing is known.
 */
export function resolveExpandCaret(captured, currentLength) {
  if (!captured) return { start: currentLength, end: currentLength };

  const drift =
    typeof captured.capturedLength === 'number'
      ? currentLength - captured.capturedLength
      : 0;

  // A collapsed caret follows the typing; a real selection is not extended by
  // it, so both ends shift together.
  const start = Math.max(0, Math.min(captured.start + drift, currentLength));
  const end = Math.max(start, Math.min(captured.end + drift, currentLength));
  return { start, end };
}

/**
 * Does the plain-text range [start, end) touch any `{{…}}` token in `text`?
 *
 * Gate for "To Variable": turning a selection that overlaps a token into a
 * new variable would nest or split braces, so the menu is not offered at all.
 * Half-open intersection (`start < tokEnd && end > tokStart`) covers a range
 * spanning a token, one strictly inside it, and one ending on its `}}` in one
 * rule; a range merely ADJACENT to a token (end === tokStart, or start ===
 * tokEnd) does not intersect. The regex is the highlighter's own
 * (createHighlightedHTML) — keep them identical.
 */
export function selectionIntersectsVariable(text, start, end) {
  const regex = /{{\s*([^}]+)\s*}}/g;
  let match;
  while ((match = regex.exec(text || '')) !== null) {
    const tokStart = match.index;
    const tokEnd = tokStart + match[0].length;
    if (start < tokEnd && end > tokStart) return true;
  }
  return false;
}

/** The URL field never holds a newline — these find the ones a browser leaves in its DOM. */
export const NEWLINE = /[\r\n]/;

export const NEWLINE_ALL = /[\r\n]+/g;
